import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useUsedProductDetailByItemId, getItemIdForUsedProduct } from '../../../hooks/useUsedProductDetail';
import { QuickViewDrawer } from './sub-components/QuickViewDrawer';

const { bool, func, shape } = PropTypes;

/**
 * UsedEquipmentQuickView
 *
 * Container component that manages:
 * - Drawer visibility state
 * - Fetching item detail data via useUsedProductDetailByItemId
 * - Passing context to QuickViewDrawer and sub-components
 *
 * Usage:
 *   const [isOpen, setIsOpen] = useState(false);
 *   const [selectedItem, setSelectedItem] = useState(null);
 *
 *   <UsedEquipmentQuickView
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *     item={selectedItem}
 *     paginateCarousel={true}
 *   />
 *
 * Props:
 *   isOpen: bool - Controls drawer visibility
 *   onClose: func - Callback when drawer closes
 *   item: shape - Used equipment item to display
 *   paginateCarousel: bool - Pass through to QuickViewDetails
 */
export const UsedEquipmentQuickView = ({
  isOpen,
  onClose,
  item,
  paginateCarousel = true
}) => {
  // Get the THD catalog itemId from the used equipment item
  const itemId = item ? getItemIdForUsedProduct(item) : null;

  // Fetch detailed product info for the item
  // IMPORTANT: Always call hooks in the same order/number, even if itemId is null
  // The hook will handle the null gracefully
  const itemDetailResult = useUsedProductDetailByItemId(itemId);
  const itemDetail = itemDetailResult?.detail || null;

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!item) {
    return null;
  }

  return (
    <QuickViewDrawer
      isOpen={isOpen}
      onClose={handleClose}
      item={item}
      itemDetail={itemDetail}
      paginateCarousel={paginateCarousel}
    />
  );
};

UsedEquipmentQuickView.displayName = 'UsedEquipmentQuickView';

UsedEquipmentQuickView.propTypes = {
  isOpen: bool.isRequired,
  onClose: func,
  item: shape({}),
  paginateCarousel: bool
};

UsedEquipmentQuickView.defaultProps = {
  onClose: () => {},
  item: null,
  paginateCarousel: true
};
